import { AxiosError } from 'axios';
import { t } from 'i18next';
import { useState } from 'react';
import { useTheme } from 'styled-components';

import AsyncTable, { Column, RequestPage } from '../../../../components/AsyncTable';
import NewRegisterBtn from '../../../../components/Buttons/NewRegisterBtn';
import { backendErrorNotification } from '../../../../components/Notification';
import HttpStatus from '../../../../enums/httpStatus';
import { apiKeysReq } from '../../../../services/requests';
import ContentContainer from '../../../../templates/Content/ContentContainer';
import { ApiKey, ApiResPaginated } from '../../../../types';
import CopyApiKeyModal from './components/CopyApiKeyModal';
import CreateOrEditApiKeyModal from './components/CreateOrEditApiKeyModal';
import DeleteApiKeyModal from './components/DeleteApiKeyModal';
import { CopyIcon, DeleteIcon, EditIcon } from '../../../../icons';
import { ActionButtonsContainer } from '../../../../components/TablePaginator/styles';

const formatApiKeyDate = (dateValue: string | null, hasExpiration: boolean) => {
  if (!hasExpiration || !dateValue) {
    return t('noExpiration');
  }

  return dateValue.replace('T', ' ').replace('.000000Z', '').replace('Z', '').slice(0, 16);
};

const ApiKeysPage = () => {
  const theme = useTheme();

  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [apiKeySelected, setApiKeySelected] = useState<ApiKey | null>(null);

  const [showFormModal, setShowFormModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);

  const [updateTable, setUpdateTable] = useState(0);

  const handleNewApiKeyBtn = () => {
    setApiKeySelected(null);
    setShowFormModal(true);
  };

  const handleTableEditBtn = (row: ApiKey) => {
    setApiKeySelected(row);
    setShowFormModal(true);
  };

  const handleTableDeleteBtn = (row: ApiKey) => {
    setApiKeySelected(row);
    setShowDeleteModal(true);
  };

  const handleTableCopyBtn = (row: ApiKey) => {
    setApiKeySelected(row);
    setShowCopyModal(true);
  };

  const getApiKeys: RequestPage = async ({ search, page, quantityPerPage }) => {
    let hasMore = true;
    const source = apiKeysReq.axios.CancelToken.source();

    try {
      apiKeysReq.query = `?search=${search}&page=${page}&quantityPerPage=${quantityPerPage}`;
      const response = await apiKeysReq.index(source.token);

      if (response.status !== HttpStatus.OK) {
        throw response;
      }

      const { data, paginated }: ApiResPaginated<ApiKey[]> = response.data;

      if (data.length === 0) {
        hasMore = false;
      }

      return {
        data,
        hasMore,
        totalPage: paginated.totalPage,
      };
    } catch (err) {
      if (!apiKeysReq.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
      }

      return {
        data: [],
        hasMore: false,
        totalPage: 0,
      };
    }
  };

  const actionsButtons = (row: ApiKey) => (
    <ActionButtonsContainer type={5}>
      <CopyIcon onClick={() => handleTableCopyBtn(row)} title={t('copy')} />
      <EditIcon onClick={() => handleTableEditBtn(row)} title={t('edit')} />
      <DeleteIcon onClick={() => handleTableDeleteBtn(row)} title={t('delete')} />
    </ActionButtonsContainer>
  );

  const columns: Column<ApiKey>[] = [
    {
      label: t('name') as string,
      accessor: 'name',
    },
    {
      label: t('apiKey') as string,
      accessor: 'masked_api_key',
    },
    {
      label: t('expirationDate') as string,
      Cell: (row) => formatApiKeyDate(row.expires_at, row.has_expiration),
    },
    {
      label: t('actions') as string,
      Cell: actionsButtons,
    },
  ];

  return (
    <ContentContainer title={t('apiKeys')}>
      <NewRegisterBtn
        onClick={handleNewApiKeyBtn}
        style={{ marginBottom: '1rem', marginLeft: '0' }}
      />

      <AsyncTable
        tableName={t('apiKeys')}
        columns={columns}
        value={apiKeys}
        onChange={setApiKeys}
        requestPage={getApiKeys}
        reqListener={[updateTable]}
        options={{
          styles: {
            primaryColor: `${theme.colors.surface}`,
            secondaryColor: `${theme.colors.onSurface}`,
            alternateRowColor: theme.colors.onSurfaceLight,
            textColor: theme.colors.text,
          },
          quantityPerPageLabel: t('quantityPerPage'),
          searchPlaceholder: t('search'),
        }}
      />

      {showFormModal && (
        <CreateOrEditApiKeyModal
          showModal={showFormModal}
          closeModal={() => setShowFormModal(false)}
          apiKeySelected={apiKeySelected}
          callback={() => setUpdateTable((currentValue) => currentValue + 1)}
        />
      )}

      {showDeleteModal && apiKeySelected && (
        <DeleteApiKeyModal
          showModal={showDeleteModal}
          closeModal={() => setShowDeleteModal(false)}
          apiKeySelected={apiKeySelected}
          callback={() => setUpdateTable((currentValue) => currentValue + 1)}
        />
      )}

      {showCopyModal && apiKeySelected && (
        <CopyApiKeyModal
          showModal={showCopyModal}
          closeModal={() => setShowCopyModal(false)}
          apiKeySelected={apiKeySelected}
        />
      )}
    </ContentContainer>
  );
};

export default ApiKeysPage;